import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'

import { useLayout } from '@hooks/useLayout'
import { LoadingPage } from '@components/shared/LoadingPage'

import type { SideBarDataProps } from '@hooks/useLayout/types'

const ALWAYS_ALLOWED_PATHS = [
  '/dashboard',
  '/auth',
  '/gestao-financeira/lancamento-consolidado',
  '/gestao-financeira/previa/gestao-da-previa'
] as const
const DEFAULT_REDIRECT_PATH = '/dashboard'

const getAllowedPaths = (sideBarData: SideBarDataProps[]): string[] => {
  return sideBarData.flatMap((parent) => {
    const childPaths = parent.children
      ?.filter((child) => child.isActive)
      .map((child) => `${parent.path}${child.path}`) ?? []

    const parentPath = parent.isActive && !parent.children
      ? [parent.path]
      : []

    return [...childPaths, ...parentPath]
  })
}

const matchesPath = (pathname: string, allowedPath: string): boolean => {
  return pathname === allowedPath || pathname.startsWith(`${allowedPath}/`)
}

export const RouteGuard = () => {
  const { pathname } = useLocation()
  const { sideBarData, isSideBarDataLoading } = useLayout()
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  useEffect(() => {
    if (!isSideBarDataLoading && sideBarData.length > 0) {
      setHasLoadedOnce(true)
    }
  }, [isSideBarDataLoading, sideBarData])

  const allowedPaths = useMemo(
    () => getAllowedPaths(sideBarData),
    [sideBarData]
  )

  const isAlwaysAllowed = ALWAYS_ALLOWED_PATHS.some((path) =>
    matchesPath(pathname, path)
  )

  if (isAlwaysAllowed) {
    return <Outlet />
  }

  if (isSideBarDataLoading || !hasLoadedOnce) {
    return <LoadingPage loading />
  }

  const hasAccess = allowedPaths.some((path) =>
    matchesPath(pathname, path)
  )

  if (!hasAccess) {
    return <Navigate to={DEFAULT_REDIRECT_PATH} replace />
  }

  return <Outlet />
}